/**
 * Platform Detection
 *
 * Detects whether running in Electron, web browser, mobile, etc.
 */

// Check if running in Electron
export const isElectron = typeof window !== 'undefined' && !!(window as any).isElectron;

// Check if electronAPI is available
export const hasElectronAPI = typeof window !== 'undefined' && !!(window as any).electronAPI;

// Get the Electron API (typed)
export function getElectronAPI() {
  if (!hasElectronAPI) return null;
  return (window as any).electronAPI as ElectronAPI;
}

// Platform type
export type Platform = 'electron-mac' | 'electron-win' | 'electron-linux' | 'web' | 'mobile';

export function getPlatform(): Platform {
  if (!isElectron) {
    // Check for mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    return isMobile ? 'mobile' : 'web';
  }

  // In Electron, check process.platform via API
  const api = getElectronAPI();
  if (api) {
    // This would need to be called async, so for sync check:
    const platform = navigator.platform.toLowerCase();
    if (platform.includes('mac')) return 'electron-mac';
    if (platform.includes('win')) return 'electron-win';
    return 'electron-linux';
  }

  return 'web';
}

// Type definitions for Electron API
export interface ElectronAPI {
  store: {
    get: (key: string) => Promise<unknown>;
    set: (key: string, value: unknown) => Promise<boolean>;
  };
  app: {
    paths: () => Promise<{ documents: string; userData: string; home: string; temp: string }>;
    info: () => Promise<{ platform: string; arch: string; version: string; isPackaged: boolean }>;
    isFirstRun: () => Promise<boolean>;
    completeFirstRun: () => Promise<boolean>;
  };
  dialog: {
    selectFolder: () => Promise<string | null>;
    selectFile: (options?: { filters?: { name: string; extensions: string[] }[] }) => Promise<string | null>;
  };
  archive: {
    port: () => Promise<number | null>;
    enabled: () => Promise<boolean>;
    enable: (archivePath?: string) => Promise<{ success: boolean; port?: number }>;
    disable: () => Promise<{ success: boolean }>;
    restart: (newPath?: string) => Promise<{ success: boolean; port?: number }>;
  };
  ollama: {
    enabled: () => Promise<boolean>;
    enable: () => Promise<boolean>;
    disable: () => Promise<boolean>;
    status: () => Promise<{ installed: boolean; running: boolean }>;
  };
  whisper: {
    status: () => Promise<WhisperStatus>;
    modelsLocal: () => Promise<WhisperModel[]>;
    modelsAvailable: () => Promise<Array<{ name: string; size: string; downloaded: boolean }>>;
    downloadModel: (modelName: string) => Promise<{ success: boolean; error?: string }>;
    transcribe: (audioPath: string, modelName?: string) => Promise<TranscribeResult>;
    onDownloadProgress: (callback: (progress: DownloadProgress) => void) => () => void;
    onTranscribeProgress: (callback: (progress: TranscribeProgress) => void) => () => void;
  };
  npe: {
    port: () => Promise<number | null>;
    status: () => Promise<NpeStatus>;
  };
  shell: {
    openExternal: (url: string) => Promise<{ success: boolean; error?: string }>;
  };
  cloudDrives: CloudDrivesAPI;
}

// Whisper types
export interface WhisperStatus {
  available: boolean;
  modelLoaded: boolean;
  currentModel: string | null;
  modelsPath: string;
  availableModels: string[];
}

export interface WhisperModel {
  name: string;
  size: string;
  path: string;
}

export interface TranscribeResult {
  success: boolean;
  result?: {
    text: string;
    segments?: Array<{ start: number; end: number; text: string }>;
    language?: string;
    duration?: number;
  };
  error?: string;
}

export interface DownloadProgress {
  model: string;
  percent: number;
  downloaded: number;
  total: number;
}

export interface TranscribeProgress {
  status: 'loading' | 'transcribing' | 'complete' | 'error';
  progress: number;
  message?: string;
}

export interface NpeStatus {
  running: boolean;
  port: number | null;
  service?: string;
  version?: string;
  ollama?: { available: boolean; url: string };
}

// Default port for development fallback
const DEFAULT_ARCHIVE_PORT = 3002;
const DEFAULT_NPE_PORT = 3003;

// Cloud API URL (from env)
const CLOUD_API_URL = import.meta.env.VITE_API_URL as string | undefined;

// Cached port values (fetched once, used everywhere)
let _archiveServerPort: number | null = null;
let _archiveServerPortPromise: Promise<number> | null = null;
let _apiBaseUrl: string | null = null;

/**
 * Get the archive server port (cached after first call)
 * In Electron: dynamically assigned port from main process
 * In browser: falls back to VITE_ARCHIVE_API_URL or default 3002
 */
export async function getArchiveServerPort(): Promise<number> {
  // Return cached value if available
  if (_archiveServerPort !== null) {
    return _archiveServerPort;
  }

  // If already fetching, wait for that promise
  if (_archiveServerPortPromise) {
    return _archiveServerPortPromise;
  }

  _archiveServerPortPromise = (async () => {
    const api = getElectronAPI();
    if (api) {
      const port = await api.archive.port();
      if (port) {
        _archiveServerPort = port;
        return port;
      }
    }

    // Fallback: check env var or use default
    const envUrl = import.meta.env.VITE_ARCHIVE_API_URL;
    if (envUrl) {
      const match = envUrl.match(/:(\d+)/);
      if (match) {
        _archiveServerPort = parseInt(match[1], 10);
        return _archiveServerPort;
      }
    }

    _archiveServerPort = DEFAULT_ARCHIVE_PORT;
    return _archiveServerPort;
  })();

  return _archiveServerPortPromise;
}

/**
 * Get archive server URL
 * Cloud: uses VITE_API_URL (same API handles archive routes)
 * Electron: uses localhost with archive server port
 * Browser dev: uses localhost with port
 */
export async function getArchiveServerUrl(): Promise<string> {
  // Cloud deployment - use the API URL for archive endpoints
  if (CLOUD_API_URL && !isElectron) {
    _apiBaseUrl = CLOUD_API_URL;
    return CLOUD_API_URL;
  }

  // Electron - use local archive server
  const port = await getArchiveServerPort();
  _apiBaseUrl = `http://localhost:${port}`;
  return _apiBaseUrl;
}

/**
 * Get archive server URL synchronously (after first async call)
 * Returns null if not yet initialized
 */
export function getArchiveServerUrlSync(): string | null {
  // Cloud deployment - use the API URL
  if (CLOUD_API_URL && !isElectron) {
    return CLOUD_API_URL;
  }

  // Electron - local archive server
  if (_archiveServerPort === null) return null;
  return `http://localhost:${_archiveServerPort}`;
}

/**
 * Initialize platform config (call early in app startup)
 * Pre-fetches port values so sync getters work
 */
export async function initPlatformConfig(): Promise<void> {
  await getArchiveServerPort();
}

/**
 * Get NPE-Local API base URL (when in Electron)
 */
export async function getNpeLocalUrl(): Promise<string | null> {
  const api = getElectronAPI();
  if (!api) return null;

  const port = await api.npe.port();
  if (!port) return null;

  return `http://localhost:${port}`;
}

/**
 * Check if NPE-Local is available
 */
export async function isNpeLocalAvailable(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;

  const status = await api.npe.status();
  return status.running;
}

export interface CloudDrivesAPI {
  listDrives: () => Promise<CloudDrive[]>;
  google: {
    connect: () => Promise<{ success: boolean; error?: string }>;
    isConnected: () => Promise<boolean>;
    disconnect: () => Promise<{ success: boolean }>;
    list: (folderId?: string, pageToken?: string) => Promise<CloudListResult>;
    search: (query: string, pageToken?: string) => Promise<CloudListResult>;
    download: (fileId: string) => Promise<{ success: boolean; content?: ArrayBuffer; error?: string }>;
  };
}

export interface CloudDrive {
  id: string;
  provider: 'google' | 'dropbox' | 'onedrive' | 's3';
  name: string;
  icon: string;
}

export interface CloudFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime?: string;
  thumbnailLink?: string;
  isFolder: boolean;
}

export interface CloudListResult {
  success: boolean;
  files?: CloudFile[];
  nextPageToken?: string;
  error?: string;
}
